import { useState, useEffect } from 'react';
import { getAuditLogs, type AuditLog } from '../api/client';
import { format } from 'date-fns';

export default function AuditPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    actor: '',
    object_type: '',
    limit: 100,
  });
  const [expandedLog, setExpandedLog] = useState<number | null>(null);

  useEffect(() => {
    loadLogs();
  }, [filters]);

  async function loadLogs() {
    setLoading(true);
    setError(null);

    const result = await getAuditLogs({
      actor: filters.actor || undefined,
      object_type: filters.object_type || undefined,
      limit: filters.limit,
    });

    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      setLogs(result.data.logs);
    }

    setLoading(false);
  }

  function getActionColor(action: string): string {
    switch (action) {
      case 'CREATE':
        return 'text-green-400';
      case 'UPDATE':
        return 'text-blue-400';
      case 'DELETE':
        return 'text-red-400';
      case 'ACTIVATE':
        return 'text-yellow-400';
      default:
        return 'text-gray-400';
    }
  }

  function getObjectTypeIcon(type: string): string {
    switch (type) {
      case 'RULESET':
        return '📜';
      case 'LIST_ENTRY':
        return '📝';
      case 'LABEL':
        return '🏷️';
      case 'REPUTATION':
        return '⭐';
      case 'LISTS':
        return '📋';
      default:
        return '📄';
    }
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Audit Log</h1>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Actor</label>
            <input
              type="text"
              className="input"
              placeholder="user@example.com"
              value={filters.actor}
              onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Object Type</label>
            <select
              className="select"
              value={filters.object_type}
              onChange={(e) => setFilters({ ...filters, object_type: e.target.value })}
            >
              <option value="">All</option>
              <option value="RULESET">Ruleset</option>
              <option value="LIST_ENTRY">List Entry</option>
              <option value="LABEL">Label</option>
              <option value="REPUTATION">Reputation</option>
              <option value="LISTS">Lists</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Limit</label>
            <select
              className="select"
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="250">250</option>
              <option value="500">500</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/20 text-red-400 p-4 rounded-lg mb-4">
          Error: {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.length === 0 ? (
            <div className="card text-center text-gray-400 py-12">
              No audit logs found
            </div>
          ) : (
            logs.map((log) => (
              <div key={log.id} className="card">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">{getObjectTypeIcon(log.object_type)}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                        <span className="text-gray-300">{log.object_type}</span>
                        <span className="text-gray-500 font-mono text-sm">
                          #{log.object_id}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">
                        by {log.actor} · {format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}
                      </div>
                    </div>
                  </div>
                  <span className="text-gray-400">{expandedLog === log.id ? '▲' : '▼'}</span>
                </div>

                {expandedLog === log.id && (
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-400 mb-2">Before</div>
                        <pre className="bg-gray-900 p-3 rounded text-xs overflow-auto max-h-48">
                          {log.before_state
                            ? JSON.stringify(JSON.parse(log.before_state), null, 2)
                            : 'null'}
                        </pre>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-2">After</div>
                        <pre className="bg-gray-900 p-3 rounded text-xs overflow-auto max-h-48">
                          {log.after_state
                            ? JSON.stringify(JSON.parse(log.after_state), null, 2)
                            : 'null'}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
